import React, { memo } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  LinearProgress,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Warning as WarningIcon } from "@material-ui/icons";
import { DroppableTrack } from "../../../../components/tracks/DroppableTrack";
import { TrackRailcarList } from "./TrackRailcarList";
import { UNSPOTTED_TRACK_ID } from "../../../../utils/constants";
import { getTrackHeaderLabel } from "../utils/trackLocationUtils";

/**
 * Optimized Track Bucket component for Crew App to isolate track re-renders
 * Displays a single track with its railcars in a droppable container
 */
export const TrackBucket = memo(
  ({
    track,
    selectedTrack,
    targetTrack,
    trackCars,
    fullCarsOnTrack,
    staleTracks,
    isDraggingGlobal,
    classes,
    onMouseEnter,
    onMouseLeave,
    // Stable props passed down to TrackRailcarList
    selectedIdsSet,
    getShipmentClassName,
    getShipmentIcon,
    canDelete,
    canAdd,
    canOpenShipment,
    isXSmall,
    openShipment,
    onShipmentClick,
    onShipmentLongPress,
    accountFilterId,
    handleDeleteConfirm,
    removeFromTrack,
    addItem,
    showPlaceAction,
    defaultShipmentId,
    currentShipment,
    selectedShipment,
    colorsLoaded,
    isLoading = false,
  }) => {
    if (!track) return null;
    const trackLocation = track.locations?.items?.[0];
    const trackLocationId = trackLocation?.id;

    const isSelectedTrack = selectedTrack?.id === track.id;
    const isTargetTrack = targetTrack?.id === track.id;
    const locCapacityUsed =
      typeof trackLocation?.capacityUsed === "number"
        ? trackLocation.capacityUsed
        : null;

    return (
      <Grid item xs={12} md={4} lg={4}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          mb={0.5}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            minWidth={0}
            flexShrink={1}
          >
            <Tooltip title={getTrackHeaderLabel(track)} arrow>
              <Typography
                variant="overline"
                align="center"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {getTrackHeaderLabel(track)}
              </Typography>
            </Tooltip>
            {staleTracks[trackLocationId] && (
              <Tooltip
                title={`Network Error: Could not refresh this track. Showing last known state. (${staleTracks[trackLocationId]})`}
              >
                <WarningIcon style={{ color: "#f57c00", fontSize: 16 }} />
              </Tooltip>
            )}
          </Box>
          {track.id !== UNSPOTTED_TRACK_ID && locCapacityUsed != null && (
            <Box
              ml={1}
              display="flex"
              alignItems="center"
              minWidth={80}
              maxWidth={160}
            >
              <LinearProgress
                variant="determinate"
                value={locCapacityUsed}
                style={{ flex: 1, marginRight: 4 }}
              />
              <Typography variant="caption" style={{ whiteSpace: "nowrap" }}>
                {locCapacityUsed}%
              </Typography>
            </Box>
          )}
        </Box>
        <DroppableTrack
          trackLocationId={trackLocationId}
          className={classes.trackContainer}
        >
          <Box position="relative" width="100%" height="100%">
            <TrackRailcarList
              trackCars={trackCars}
              fullCarsOnTrack={fullCarsOnTrack}
              trackLocationId={trackLocationId}
              isDraggingGlobal={isDraggingGlobal}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              selectedIdsSet={selectedIdsSet}
              getShipmentClassName={getShipmentClassName}
              getShipmentIcon={getShipmentIcon}
              classes={classes}
              canDelete={canDelete}
              canAdd={canAdd}
              canOpenShipment={canOpenShipment}
              isXSmall={isXSmall}
              openShipment={openShipment}
              onShipmentClick={onShipmentClick}
              onShipmentLongPress={onShipmentLongPress}
              accountFilterId={accountFilterId}
              handleDeleteConfirm={handleDeleteConfirm}
              removeFromTrack={removeFromTrack}
              addItem={addItem}
              showPlaceAction={showPlaceAction}
              defaultShipmentId={defaultShipmentId}
              currentShipment={currentShipment}
              selectedShipment={selectedShipment}
              colorsLoaded={colorsLoaded}
            />
            {isLoading && (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{ pointerEvents: "none" }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
        </DroppableTrack>
      </Grid>
    );
  },
  (prev, next) => {
    return (
      prev.track === next.track &&
      prev.selectedTrack === next.selectedTrack &&
      prev.targetTrack === next.targetTrack &&
      prev.trackCars === next.trackCars &&
      prev.fullCarsOnTrack === next.fullCarsOnTrack &&
      prev.staleTracks === next.staleTracks &&
      prev.isDraggingGlobal === next.isDraggingGlobal &&
      prev.classes === next.classes &&
      prev.onMouseEnter === next.onMouseEnter &&
      prev.onMouseLeave === next.onMouseLeave &&
      // Stable props check
      prev.selectedIdsSet === next.selectedIdsSet &&
      prev.getShipmentClassName === next.getShipmentClassName &&
      prev.getShipmentIcon === next.getShipmentIcon &&
      prev.canDelete === next.canDelete &&
      prev.canAdd === next.canAdd &&
      prev.canOpenShipment === next.canOpenShipment &&
      prev.isXSmall === next.isXSmall &&
      prev.openShipment === next.openShipment &&
      prev.onShipmentClick === next.onShipmentClick &&
      prev.onShipmentLongPress === next.onShipmentLongPress &&
      prev.accountFilterId === next.accountFilterId &&
      prev.handleDeleteConfirm === next.handleDeleteConfirm &&
      prev.removeFromTrack === next.removeFromTrack &&
      prev.addItem === next.addItem &&
      prev.showPlaceAction === next.showPlaceAction &&
      prev.defaultShipmentId === next.defaultShipmentId &&
      prev.currentShipment === next.currentShipment &&
      prev.selectedShipment === next.selectedShipment &&
      prev.colorsLoaded === next.colorsLoaded
    );
  },
);

TrackBucket.displayName = "TrackBucket";
